import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Message,
  Segment,
} from 'semantic-ui-react';
import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { dangerNotification } from '../../services/notification';
import { toAbsoluteUrl } from '../../utils/toAbsoluteUrl';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import Button from '../../components/Button';

import {
  initialFilters,
  standaloneChargesLabel,
  statusLabelMap,
  statusOptions
} from './constants';
import { StandaloneChargesTab } from './types';
import { useStandaloneChargesList } from './hooks/useStandaloneChargesList';
import { useChargeActions } from './hooks/useChargeActions';
import { useStandaloneChargesActivation } from './hooks/useStandaloneChargesActivation';
import { useCreateStandaloneChargeForm } from './hooks/useCreateStandaloneChargeForm';
import CreateChargeModal from './components/CreateChargeModal';
import SuccessModal from './components/SuccessModal';
import CancelChargeModal from './components/CancelChargeModal';
import FiltersBar from './components/FiltersBar';
import ChargesTable from './components/ChargesTable';
import ChargesTabs from './components/ChargesTabs';
import ChargeDetailsModal from './components/ChargeDetailsModal';
import KycBanner from './components/KycBanner';
import ActivationGate from './components/ActivationGate/ActivationGate';
import ActivationStepContent from './components/ActivationGate/ActivationStepContent';
import CommissioningChargesList from './components/CommissioningChargesList';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;


const StandaloneChargesPage = ({
  availableBrokersAndEstates,
  user
}: Props) => {
  const lastActivationStep = 5;
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdChargeLink, setCreatedChargeLink] = useState('');
  const [createdPaymentMethod, setCreatedPaymentMethod] = useState<'PIX' | 'BOLETO'>('PIX');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsChargeId, setDetailsChargeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StandaloneChargesTab>('sent');
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const isBrokerAdmin = role === UserRolesEnum.brokerAdmin;
  const isEstateAdmin = role === UserRolesEnum.estateAdmin;
  const isAdminUser = role === UserRolesEnum.brokerAdmin || role === UserRolesEnum.brokerAnalyst;
  const needsActivationGate = isEstateUser;

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map((item: CustomDropDownOptions) => {
      const valueId =
        typeof item.value === 'string' ? item.value : item.value?.id;
      return {
        key: valueId || String(item.key),
        text: item.text || '',
        value: valueId || ''
      };
    });
  }, [availableBrokersAndEstates?.estates]);

  const activationEstateId = useMemo(() => {
    return (
      availableBrokersAndEstates?.activeEstateId ||
      user?.estate?.id ||
      ''
    );
  }, [
    availableBrokersAndEstates?.activeEstateId,
    user?.estate?.id
  ]);

  const activation = useStandaloneChargesActivation({
    needsActivationGate,
    activationEstateId,
    appliedFilters: initialFilters,
    onEnabled: () => {
      // list hook will be called below; we only need to refresh after enabling
    }
  });

  const {
    activationStatus,
    activationStatusError,
    activationLoading,
    activationSaving,
    activationForm,
    activationWizardOpen,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    canCreateCharges,
    activationIsPendingKyc,
    activationKycLink,
    activationKycPageUrl,
    activationKycQrImageUrl,
    loadActivationStatus,
    updateActivationFormField,
    openActivationWizard,
    closeActivationWizard,
    handleNextActivationStep,
    handleBackActivationStep,
    handleZipCodeLookup,
    handleActivate
  } = activation;

  const userHasCreatePermission =
    isAdminUser ||
    (isEstateUser && canCreateCharges);
  const userCanSend = isAdminUser || isEstateUser;
  const userCanCancel = isAdminUser || isEstateUser;
  const userCanRenew = userHasCreatePermission;

  const {
    isLoading,
    items,
    total,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    appliedFilters,
    loadData,
    handleSearch,
    clearFilters
  } = useStandaloneChargesList({ initialFilters, enabled: canCreateCharges });

  const {
    actionLoading,
    copyChargeLink,
    handleResendEmail,
    cancelModalOpen,
    chargeToCancel,
    openCancelModal,
    closeCancelModal,
    confirmCancelCharge,
    handleRenewCharge
  } = useChargeActions(
    () => loadData(),
    renewedCharge => {
      setCreatedChargeLink(renewedCharge.chargeLinkUrl);
      setCreatedPaymentMethod(renewedCharge.paymentMethod);
      setSuccessModalOpen(true);
    }
  );

  // activation hook already declared above to make isStandaloneChargesEnabled available

  const needsOnboarding =
    !canCreateCharges || activationStatus?.activation == null;
  const isCreateBlockedByActivation = needsActivationGate && !canCreateCharges;
  const isCreateBlockedByPendingKyc = needsActivationGate && activationIsPendingKyc;
  const isCreateBlocked = isCreateBlockedByActivation || isCreateBlockedByPendingKyc;

  const getCreateBlockMessage = () => {
    if (isCreateBlockedByPendingKyc) {
      return 'Conclua a biometria da Pagar.me para liberar a emissão de cobranças.';
    }
    if (isCreateBlockedByActivation) {
      return 'Conclua o cadastro de ativação para emitir cobranças.';
    }
    return '';
  };

  const createChargeForm = useCreateStandaloneChargeForm({
    isEstateUser,
    defaultEstateId: filters.estateId || '',
    isCreateBlocked,
    getCreateBlockMessage,
    onCreated: (chargeLinkUrl: string, paymentMethod: 'PIX' | 'BOLETO') => {
      setCreatedChargeLink(chargeLinkUrl);
      setCreatedPaymentMethod(paymentMethod);
      setSuccessModalOpen(true);
      setCurrentPage(1);
      loadData(1, appliedFilters);
    }
  });

  useEffect(() => {
    if (!createChargeForm.createModalOpen) return;
    setTimeout(() => {
      const firstField = document.getElementById('create-amount-input');
      firstField?.focus();
    }, 0);
  }, [createChargeForm.createModalOpen]);

  const openChargeDetails = (chargeId: string) => {
    setDetailsChargeId(chargeId);
    setDetailsModalOpen(true);
  };

  const closeChargeDetails = () => {
    setDetailsModalOpen(false);
    setDetailsChargeId(null);
  };

  const openKycLink = () => {
    if (!activationKycLink) {
      dangerNotification('Atenção', 'Link de biometria indisponível no momento.');
      return;
    }
    window.open(toAbsoluteUrl(activationKycLink), '_blank', 'noopener,noreferrer');
  };

  if (needsActivationGate && needsOnboarding) {
    return (
      <ActivationGate
        activationLoading={activationLoading}
        activationStatusError={activationStatusError}
        activationStatus={activationStatus}
        activationIsPendingKyc={activationIsPendingKyc}
        activationKycLink={activationKycLink}
        activationKycPageUrl={activationKycPageUrl}
        activationKycQrImageUrl={activationKycQrImageUrl}
        openKycLink={openKycLink}
        loadActivationStatus={loadActivationStatus}
        activationWizardOpen={activationWizardOpen}
        closeActivationWizard={closeActivationWizard}
        openActivationWizard={openActivationWizard}
        activationStep={activationStep}
        lastActivationStep={lastActivationStep}
        activationSaving={activationSaving}
        activationStepError={activationStepError}
        recipientType={activationForm.recipientType}
        handleBackActivationStep={handleBackActivationStep}
        handleNextActivationStep={handleNextActivationStep}
        handleActivate={handleActivate}
      >
        <ActivationStepContent
          activationStep={activationStep}
          activationForm={activationForm}
          updateActivationFormField={updateActivationFormField}
          handleZipCodeLookup={handleZipCodeLookup}
          addressLookupLoading={addressLookupLoading}
          addressLookupError={addressLookupError}
          addressAutoFilled={addressAutoFilled}
        />
      </ActivationGate>
    );
  }

  return (
    <>
      {activationIsPendingKyc && (
        <KycBanner
          openKycLink={openKycLink}
          loadActivationStatus={loadActivationStatus}
          activationLoading={activationLoading}
          activationKycPageUrl={activationKycPageUrl}
        />
      )}

      <ChargesTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'sent' ? (
        <>
          <FiltersBar
            isEstateUser={isEstateUser}
            estateOptions={estateOptions}
            filters={filters}
            setFilters={setFilters}
            statusOptions={statusOptions}
            onSearch={handleSearch}
            onClear={clearFilters}
          />

          <Segment>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{standaloneChargesLabel}</h3>
              {userHasCreatePermission && (
                <Button
                  type="button"
                  text="Nova Cobrança"
                  onClick={createChargeForm.openCreateModal}
                  disabled={isCreateBlocked}
                />
              )}
            </div>
            {isCreateBlocked && (
              <Message warning style={{ marginTop: 0 }}>
                <p style={{ margin: 0 }}>{getCreateBlockMessage()}</p>
              </Message>
            )}

            <ChargesTable
              isLoading={isLoading}
              items={items}
              statusLabelMap={statusLabelMap}
              currentPage={currentPage}
              total={total}
              setCurrentPage={setCurrentPage}
              actionLoading={actionLoading}
              userCanSend={userCanSend}
              userCanCancel={userCanCancel}
              userCanRenew={userCanRenew}
              showEstateColumn={!isEstateUser}
              onRowClick={openChargeDetails}
              onCopyLink={copyChargeLink}
              onResendEmail={handleResendEmail}
              onCancel={openCancelModal}
              onRenew={handleRenewCharge}
            />
          </Segment>
        </>
      ) : (
        <CommissioningChargesList
          isBrokerAdmin={isBrokerAdmin}
          isEstateAdmin={isEstateAdmin}
          defaultEstateId={activationEstateId}
          estateOptions={estateOptions}
          currentUserId={user?.id}
          currentUserDocument={user?.document}
        />
      )}

      <CreateChargeModal
        open={createChargeForm.createModalOpen}
        onClose={createChargeForm.closeCreateModal}
        isEstateUser={isEstateUser}
        estateOptions={estateOptions}
        defaultEstateId={activationEstateId}
        form={createChargeForm.createForm}
        errors={createChargeForm.createFormErrors}
        saving={createChargeForm.savingCreate}
        onChangeField={createChargeForm.updateCreateFormField}
        onSubmit={createChargeForm.handleCreateCharge}
        addressLookupLoading={createChargeForm.addressLookupLoading}
        addressLookupError={createChargeForm.addressLookupError}
        addressAutoFilled={createChargeForm.addressAutoFilled}
        onPayerAddressZipCodeInput={createChargeForm.onPayerAddressZipCodeInput}
      />

      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        chargeLink={createdChargeLink}
        paymentMethod={createdPaymentMethod}
        onCopy={() => copyChargeLink(createdChargeLink)}
      />

      <CancelChargeModal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        charge={chargeToCancel}
        loading={Boolean(actionLoading?.action === 'cancel')}
        onConfirm={confirmCancelCharge}
      />

      <ChargeDetailsModal
        open={detailsModalOpen}
        chargeId={detailsChargeId}
        statusLabelMap={statusLabelMap}
        hideEstateSection={isEstateUser}
        onClose={closeChargeDetails}
        onCopyLink={copyChargeLink}
      />
    </>
  );
};

export default connect(mapState)(StandaloneChargesPage);
